import * as React from 'react';
import capitalize from 'lodash/capitalize';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { FormField } from 'components/form';
import InputRadio from 'components/inputs/input-radio/InputRadio';

import OfferAdditionalCompensationVoucher from './voucher/OfferAdditionalCompensationVoucher';
import OfferAdditionalCompensationOther from './other/OfferAdditionalCompensationOther';

import { __t, applyTranslations } from 'utils/language';
import { isRequired } from 'utils/validators';
import {
  OFFER_ADDITIONAL_COMPENSATION_NO,
  OFFER_ADDITIONAL_COMPENSATION_YES
} from './utils/offer-additional-compensation';

import './offer-additional-compensation.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./offer-additional-compensation.i18n.json'));

import type { SubcampaignOfferStatus } from 'types';
import type {
  OfferAdditionalCompensationData,
  OfferAdditionalCompensationOtherType,
  OfferAdditionalCompensationToggle,
  OfferAdditionalCompensationVoucherType
} from './types';

interface Props {
  additionalCompensation: OfferAdditionalCompensationData, // eslint-disable-line react/no-unused-prop-types
  offerState: SubcampaignOfferStatus,
  onChange: (values: OfferAdditionalCompensationData) => Promise<void>
}

interface State {
  compensation: {
    value: OfferAdditionalCompensationToggle
  },
  voucher: OfferAdditionalCompensationVoucherType,
  other: OfferAdditionalCompensationOtherType
}

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'offer_additional_compensation';

const EMPTY_VOUCHER_STATE = {
  isSelected: false,
  amount: '',
  description: ''
};

const EMPTY_OTHER_STATE = {
  isSelected: false,
  description: ''
};

const TOGGLE_TYPES = [
  OFFER_ADDITIONAL_COMPENSATION_YES,
  OFFER_ADDITIONAL_COMPENSATION_NO
];

class OfferAdditionalCompensation extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = this.getDefaultState(props);
  }

  getDefaultState = (
    nextProps: Props
  ): State => {
    const { additionalCompensation } = nextProps;
    const voucherAmount = additionalCompensation.voucherAmount;
    const hasVoucher = !isNil(voucherAmount) && !isEmpty(String(voucherAmount));
    const hasOther = !isEmpty(additionalCompensation.otherDescription);

    return {
      compensation: {
        value: hasVoucher || hasOther
          ? OFFER_ADDITIONAL_COMPENSATION_YES
          : OFFER_ADDITIONAL_COMPENSATION_NO
      },
      voucher: {
        isSelected: hasVoucher,
        amount: !isNil(voucherAmount) ? voucherAmount : '',
        description: additionalCompensation.voucherDescription
      },
      other: {
        isSelected: hasOther,
        description: additionalCompensation.otherDescription
      }
    };
  };

  getResetState = (): State => ({
    compensation: {
      value: OFFER_ADDITIONAL_COMPENSATION_NO
    },
    voucher: EMPTY_VOUCHER_STATE,
    other: EMPTY_OTHER_STATE
  });

  getUpdatedData = (
    updatedIdentifier?: string,
    value?: string
  ): OfferAdditionalCompensationData => {
    const { compensation, voucher, other } = this.state;
    const hasAdditionalCompensation = compensation.value === OFFER_ADDITIONAL_COMPENSATION_YES;
    let voucherAmount = String(voucher.amount).trim();
    let voucherDescription = voucher.description.trim();
    let otherDescription = other.description.trim();

    if (!voucher.isSelected || !hasAdditionalCompensation) {
      voucherAmount = '';
      voucherDescription = '';
    }

    if (!other.isSelected || !hasAdditionalCompensation) {
      otherDescription = '';
    }

    const defaults = {
      voucherAmount,
      voucherDescription,
      otherDescription
    };

    if (hasAdditionalCompensation && (!isNil(updatedIdentifier) && !isNil(value))) {
      return {
        ...defaults,
        [updatedIdentifier]: value
      };
    }

    return defaults;
  };

  handleRadioChange = (
    event: React.SyntheticEvent<HTMLInputElement>
  ): void => {
    const { offerState, onChange } = this.props;
    const target = event.currentTarget;
    const identifier = target.name;
    const value = target.value;

    this.setState(state => ({
      ...state,
      [identifier]: {
        value
      }
    }), () => {
      if (value === OFFER_ADDITIONAL_COMPENSATION_NO && offerState === 'draft') {
        this.setState(this.getResetState());
      }

      onChange(this.getUpdatedData());
    });
  };

  handleCheckboxChange = (
    identifier: string,
    isSelected: boolean
  ): void => {
    const { offerState, onChange } = this.props;

    this.setState(state => ({
      ...state,
      [identifier]: {
        ...state[identifier],
        isSelected
      }
    }), () => {
      const { voucher, other } = this.state;
      const isDraft = offerState === 'draft';

      this.setState(state => ({
        ...state,
        voucher: !voucher.isSelected && isDraft ? EMPTY_VOUCHER_STATE : state.voucher,
        other: !other.isSelected && isDraft ? EMPTY_OTHER_STATE : state.other
      }), () => onChange(this.getUpdatedData()));
    });
  };

  handleInputChange = (
    identifier: string,
    field: string,
    value: string
  ): void => {
    const { onChange } = this.props;

    this.setState(state => ({
      ...state,
      [identifier]: {
        ...state[identifier],
        [field]: value
      }
    }), () => {
      const updatedIdentifier = `${identifier}${capitalize(field)}`;
      const updatedData = this.getUpdatedData(updatedIdentifier, value);

      onChange(updatedData);
    });
  };

  renderToggleSection = (): React.ReactNode => (
    <div className="offer-additional-compensation__toggle">
      <FormField
        label={__t(`${MSG_KEY}.titles.additional_compensation_included`)}
        labelType="highlighted"
        direction="column"
        fontSize="small"
        showValidationIcon={false}
      >
        {
          TOGGLE_TYPES.map((type: OfferAdditionalCompensationToggle) => (
            <InputRadio
              key={`offer-additional-compensation__toggle--${type}`}
              id={`offer-additional-compensation__toggle--${type}`}
              name="compensation"
              value={String(type)}
              checked={this.state.compensation.value === type}
              label={__t(`${COMMON_MSG_KEY}.basics.${type}`)}
              onChange={this.handleRadioChange}
            />
          ))
        }
      </FormField>
    </div>
  );

  renderVoucherSection = (): React.ReactNode => {
    const { voucher } = this.state;

    return (
      <div className="offer-additional-compensation__voucher">
        <OfferAdditionalCompensationVoucher
          voucher={voucher}
          onCheckboxChange={this.handleCheckboxChange}
          onInputChange={this.handleInputChange}
        />
      </div>
    );
  };

  renderOtherSection = (): React.ReactNode => {
    const { other } = this.state;

    return (
      <div className="offer-additional-compensation__other">
        <OfferAdditionalCompensationOther
          other={other}
          onCheckboxChange={this.handleCheckboxChange}
          onInputChange={this.handleInputChange}
        />
      </div>
    );
  };

  renderCompensationSection = (): React.ReactNode => {
    const { voucher, other } = this.state;

    return (
      <div className="offer-additional-compensation__compensation">
        <FormField
          label={__t(`${MSG_KEY}.titles.nature_of_compensation`)}
          labelType="highlighted"
          direction="column"
          fontSize="small"
          showRequiredMark
          showValidationIcon={false}
          errorClassName="offer-additional-compensation__error"
          validators={[
            isRequired(
              (voucher.isSelected || other.isSelected),
              __t(`${MSG_KEY}.errors.nature_of_compensation`)
            )
          ]}
        >
          {this.renderVoucherSection()}
          {this.renderOtherSection()}
        </FormField>
      </div>
    );
  };

  render(): React.ReactNode {
    const { compensation } = this.state;

    return (
      <div className="offer-additional-compensation">
        {this.renderToggleSection()}
        {
          compensation.value === OFFER_ADDITIONAL_COMPENSATION_YES &&
            this.renderCompensationSection()
        }
      </div>
    );
  }
}

export default OfferAdditionalCompensation;
